"use client";

import { motion } from "framer-motion";
import { FounderTrustPanel } from "@/components/common/FounderTrustPanel";
import { Container } from "@/components/common/Container";
import { SectionHeading } from "@/components/common/SectionHeading";
import { Layout } from "@/components/layout/Layout";
import { ProjectCard } from "@/components/projects/ProjectCard";
import { projects } from "@/content/projects";

export default function ProjectsPage() {
  return (
    <Layout title="Swargaseema Projects | Open Plots near Shadnagar">
      <main>
        <PageHero eyebrow="Projects" title="Verified plotted communities, presented for comparison." copy="Browse Swargaseema projects with approval, location, acreage, layout previews, and enquiry context." />
        <section className="bg-[var(--cream)] py-10 lg:py-14">
          <Container>
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6 }}
            >
              <FounderTrustPanel
                compact
                image="/brand/founder-trust-tan.webp"
                title="Compare projects with visible leadership behind every recommendation."
                copy="Swargaseema keeps the decision process direct: project facts first, documentation clarity next, and guided site visits with the team."
              />
            </motion.div>
          </Container>
        </section>
        <section className="py-16 lg:py-24">
          <Container>
            <SectionHeading
              eyebrow="All communities"
              title="Choose the project that fits your plan."
              copy="The project model is data-driven, so every new layout can be added once and reused across the site."
            />
            <div className="mt-9 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {projects.map((project, index) => (
                <motion.div 
                  key={project.slug}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <ProjectCard project={project} />
                </motion.div>
              ))}
            </div>
          </Container>
        </section>
      </main>
    </Layout>
  );
}

function PageHero({ eyebrow, title, copy }: { eyebrow: string; title: string; copy: string }) {
  return (
    <section className="bg-[var(--ink)] px-0 pb-20 pt-32 text-[var(--white)] lg:pt-40">
      <Container>
        <p className="mb-3 text-xs font-black uppercase tracking-[0.14em] text-[var(--gold-strong)]">{eyebrow}</p>
        <motion.h1 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="max-w-4xl break-words font-serif text-3xl font-medium leading-tight sm:text-5xl lg:text-6xl sm:leading-none"
        >
          {title}
        </motion.h1>
        <p className="mt-5 max-w-2xl text-base leading-8 text-white/75 sm:text-lg">{copy}</p>
      </Container>
    </section>
  );
}
